export type ManualAIAvailability =
  | "sign_in"
  | "refresh_usage"
  | "view_plans"
  | "ready";

export function resolveManualAIAvailability(input: {
  signedIn: boolean;
  usageLoaded: boolean;
  remainingCredits: number;
}): ManualAIAvailability {
  if (!input.signedIn) return "sign_in";
  if (!input.usageLoaded) return "refresh_usage";
  if (input.remainingCredits <= 0) return "view_plans";
  return "ready";
}

export type BotCreationAction =
  | "sign_in"
  | "wait"
  | "refresh_usage"
  | "view_plans"
  | "create";

export function resolveBotCreationAction(input: {
  signedIn: boolean;
  busy: boolean;
  usageLoaded: boolean;
  currentBots: number;
  maximumBots: number;
}): BotCreationAction {
  if (!input.signedIn) return "sign_in";
  if (input.busy) return "wait";
  if (!input.usageLoaded) return "refresh_usage";
  if (input.maximumBots <= 0 || input.currentBots >= input.maximumBots) return "view_plans";
  return "create";
}

export type CustomDateRecovery =
  | {
      kind: "invalid_order";
      startDate: string;
      endDate: string;
    }
  | {
      kind: "use_allowed_start";
      requestedStartDate: string;
      effectiveStartDate: string;
      endDate: string;
      maximumDays: number;
      planLimited: boolean;
    };

const ISO_INPUT_DATE = /^\d{4}-\d{2}-\d{2}$/;

function inputDateMilliseconds(value: string): number | undefined {
  if (!ISO_INPUT_DATE.test(value)) return undefined;
  const milliseconds = Date.parse(`${value}T00:00:00Z`);
  return Number.isFinite(milliseconds) ? milliseconds : undefined;
}

function inputDateString(milliseconds: number): string {
  return new Date(milliseconds).toISOString().slice(0, 10);
}

export function resolveCustomDateRecovery(input: {
  period: string;
  startDate: string;
  endDate: string;
  maximumDays: number;
  planMaximumDays?: number;
  candleMaximumDays: number;
}): CustomDateRecovery | undefined {
  if (input.period !== "Custom" || !input.startDate || !input.endDate) return undefined;
  const start = inputDateMilliseconds(input.startDate);
  const end = inputDateMilliseconds(input.endDate);
  if (start == null || end == null) return undefined;
  if (start > end) {
    return { kind: "invalid_order", startDate: input.startDate, endDate: input.endDate };
  }

  const maximumDays = Math.max(1, Math.floor(input.maximumDays));
  const earliest = end - maximumDays * 86_400_000;
  if (start >= earliest) return undefined;
  return {
    kind: "use_allowed_start",
    requestedStartDate: input.startDate,
    effectiveStartDate: inputDateString(earliest),
    endDate: input.endDate,
    maximumDays,
    planLimited:
      input.planMaximumDays != null
      && input.planMaximumDays <= input.candleMaximumDays
      && input.planMaximumDays <= maximumDays,
  };
}
